import * as Yup from 'yup';
import { ASSIGNMENTS, IDB_ATTRIBUTES, ROOT_INTEGRATION_TYPES, SCANNERS } from './constants';
import patterns from '../../../constants/patterns';
import { REQUEST_ASSIGNMENT_IN_OFFICE } from '../../../constants';
import { caseNumberSchema, createValidationSchema, patientNameSchema } from '../validation';

const idbSchema = {
  first_name: patientNameSchema,
  last_name: patientNameSchema,
  case_number_text: caseNumberSchema,
  intraoral_scan_date: Yup.date().required('Required'),
  submission_completed_by: Yup.number().required('Required'),
  rush_case: Yup.boolean(),
  rush_case_authorized_by: Yup.string().when('rush_case', {
    is: val => val === true,
    then: Yup.string()
      .required('Required')
      .max(100)
      .matches(
        patterns.name,
        'Name may only contain letters (first letter uppercase), spaces, points, brackets, and apostrophes.'
      )
  }),
  scan_submission_type: Yup.number().required('Required'),
  jaws_id: Yup.number().required('Required'),
  custom_scanner_type_name: Yup.string().when('scanner_type', {
    is: val => val === SCANNERS.OTHER,
    then: Yup.string().required('Required')
  }),
  stl_files: Yup.array().when('scan_submission_type', {
    is: val => {
      return Number(val) === ASSIGNMENTS.ORTHOSELECT;
    },
    then: Yup.array().when(IDB_ATTRIBUTES.SAVED_FOR_LATER, {
      is: true,
      then: Yup.array().nullable(),
      otherwise: Yup.array()
        .of(
          Yup.object().shape({
            file_path: Yup.string().required('Required'),
            name: Yup.string().required('Required')
          })
        )
        .required('Required')
        .min(1)
    }),
    otherwise: validationSchema => validationSchema.nullable()
  }),
  arch_wires: Yup.array(),
  rx_intraoral_photos: Yup.array().when(IDB_ATTRIBUTES.SAVED_FOR_LATER, {
    is: true,
    then: Yup.array().nullable(),
    otherwise: Yup.array()
      .of(
        Yup.object().shape({
          file_path: Yup.string().required('Required'),
          name: Yup.string().required('Required')
        })
      )
      .required('Required')
      .min(1)
  }),
  export_type: Yup.number().required(),
  print_assignment: Yup.number().required(),
  tray_design_assignment: Yup.number().nullable(),
  insert_brackets_assignment: Yup.number().required(),
  location_id: Yup.number().required(),
  shipping_method: Yup.string()
    .nullable()
    .when('print_assignment', {
      is: val => val !== ASSIGNMENTS.IN_OFFICE,
      then: Yup.string().required()
    }),
  shipping_location_id: Yup.string()
    .nullable()
    .when('print_assignment', {
      is: val => val !== ASSIGNMENTS.IN_OFFICE,
      then: Yup.string().required('Required')
    }),
  root_integration_type: Yup.number().required(),
  dicom_files: Yup.array().when(IDB_ATTRIBUTES.ROOT_INTEGRATION_TYPE, {
    is: ROOT_INTEGRATION_TYPES.DICOM,
    then: Yup.array().when(IDB_ATTRIBUTES.SAVED_FOR_LATER, {
      is: true,
      then: Yup.array().nullable(),
      otherwise: Yup.array()
        .of(
          Yup.object().shape({
            file_path: Yup.string().required('Required'),
            name: Yup.string().required('Required')
          })
        )
        .required('Required')
        .min(1)
        .max(1)
    }),
    otherwise: validationSchema => validationSchema.nullable()
  }),
  bonding_date: Yup.date().nullable()
};

export const idbBaseSchema = idbSchema;

export const createIdbValidationSchema = (
  attributes = [],
  overrideOptions = {},
  newControls = []
) => {
  const options = { requireBracketLibraries: false, ...overrideOptions };
  const schema = { ...idbSchema };

  if (options.requireBracketLibraries) {
    schema.bracket_libraries = Yup.array().when(IDB_ATTRIBUTES.INITIAL_CASE_SETUP_ASSIGNMENT, {
      is: assignment => assignment === REQUEST_ASSIGNMENT_IN_OFFICE,
      then: validationSchema => validationSchema.nullable(),
      otherwise: validationSchema => validationSchema.required().min(1)
    });
  }

  const targetAttributes = [...attributes];
  if (attributes.find(item => item === IDB_ATTRIBUTES.INITIAL_CASE_SETUP_ASSIGNMENT)) {
    targetAttributes.push(IDB_ATTRIBUTES.STL_FILES);
  }
  if (
    targetAttributes.find(item => item === IDB_ATTRIBUTES.ROOT_INTEGRATION_TYPE) ||
    attributes.find(item => item === IDB_ATTRIBUTES.ROOT_INTEGRATION_TYPE)
  ) {
    targetAttributes.push(IDB_ATTRIBUTES.DICOM_FILES);
  }

  return createValidationSchema(schema, targetAttributes, overrideOptions, newControls);
};
